import { match } from '@frontend/duck-tape';
import type { ReactNode } from '@frontend/react';
import { AppShell, Wordmark, XStack, YStack } from '@frontend/web-react';
import type { ClassName } from '@frontend/web-utils';
import { applyClass, concatClasses, safeCss } from '@frontend/web-utils';
import { NavLinkWrapper } from '../NavLinks';

export type UnauthenticatedAppHeaderProps = {
  asAppShell?: boolean;
  className?: ClassName;
  color?: 'blue' | 'yellow';
  isHidden?: boolean;
  linkTo?: string | false;
  rightContent?: ReactNode;
  transparent?: boolean;
};
export const UnauthenticatedAppHeader = ({
  asAppShell = true,
  className,
  color = 'blue',
  isHidden = false,
  linkTo = '/login',
  rightContent,
  transparent = true,
}: UnauthenticatedAppHeaderProps) => {
  const parentContainerClass = concatClasses(
    'px-sm flex',
    applyClass(transparent, 'bg-transparent'),
    applyClass(isHidden, 'hidden'),
    className,
  );

  const wordmark = (
    <Wordmark
      className={match(color)
        .with('blue', () => safeCss('fill-textPrimary'))
        .with('yellow', () => safeCss('fill-textAccent'))
        .exhaustive()}
      height={20}
      width={90}
    />
  );

  const linkedWordmark = linkTo ? (
    <NavLinkWrapper aria-label="Duckbill home" to={linkTo}>
      {wordmark}
    </NavLinkWrapper>
  ) : (
    wordmark
  );

  const mainContent = () => (
    <YStack className={concatClasses(parentContainerClass, 'py-sm', applyClass(!!rightContent, 'w-full'))}>
      <YStack className="p-md" justifyContent="center">
        {rightContent ? (
          <XStack alignItems="center" justifyContent="between">
            {linkedWordmark}
            {rightContent}
          </XStack>
        ) : (
          linkedWordmark
        )}
      </YStack>
    </YStack>
  );

  return asAppShell ? (
    <AppShell.Header className={concatClasses(parentContainerClass, 'items-center')} withBorder={false}>
      {mainContent()}
    </AppShell.Header>
  ) : (
    mainContent()
  );
};
